import { useEffect, useState } from "react";
import { getMe, linkAnonymous, type UserRow } from "./lib/api";
import { initAuth, handleCallback, subscribe } from "./lib/auth";
import { SYSTEM_PROMPT, TEACHER_NAME } from "./lib/persona";
import { Onboarding } from "./components/Onboarding";
import { Dashboard } from "./components/Dashboard";
import { Lesson } from "./components/Lesson";
import { AuthButton } from "./components/AuthButton";

type View = "loading" | "callback" | "onboarding" | "dashboard" | "lesson";

async function refreshUser(): Promise<UserRow | null> {
  try {
    const { user } = await getMe();
    return user;
  } catch {
    return null;
  }
}

export function App(): JSX.Element {
  const [view, setView] = useState<View>("loading");
  const [user, setUser] = useState<UserRow | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void (async () => {
      try {
        const isCallback = window.location.pathname === "/auth/callback";
        if (isCallback) {
          setView("callback");
        }

        await initAuth();

        if (isCallback) {
          try {
            await handleCallback();
            await linkAnonymous().catch(() => null);
            window.history.replaceState({}, "", "/");
          } catch (err) {
            setError(`Sign-in failed: ${err instanceof Error ? err.message : String(err)}`);
          }
        }

        const u = await refreshUser();
        setUser(u);
        if (!u || u.level === null || u.level === undefined) setView("onboarding");
        else setView("dashboard");
      } catch (err) {
        setError(err instanceof Error ? err.message : String(err));
        setView("onboarding");
      }
    })();

    const off = subscribe(async () => {
      const u = await refreshUser();
      setUser(u);
      if (!u || u.level === null || u.level === undefined) setView("onboarding");
      else setView("dashboard");
    });
    return off;
  }, []);

  if (view === "loading" || view === "callback") {
    return (
      <div className="h-full flex items-center justify-center bg-gradient-to-br from-ink-900 via-ink-800 to-ink-900">
        <div className="flex flex-col items-center gap-3">
          <div className="w-10 h-10 border-2 border-ink-600 border-t-lime-400 rounded-full animate-spin" />
          <div className="text-ink-400 text-xs uppercase tracking-widest">
            {view === "callback" ? "Signing you in…" : "Loading…"}
          </div>
          {error && (
            <div className="bg-red-900/40 border border-red-700 text-red-200 rounded-xl px-4 py-2 text-xs mt-3 max-w-md">
              {error}
            </div>
          )}
        </div>
      </div>
    );
  }

  if (view === "onboarding") {
    return (
      <div className="relative h-full">
        <div className="absolute top-3 right-3 z-50">
          <AuthButton variant="header" />
        </div>
        <Onboarding
          teacherName={TEACHER_NAME}
          onDone={(u) => {
            setUser(u);
            setView("dashboard");
          }}
        />
      </div>
    );
  }

  if (view === "dashboard" && user) {
    return (
      <div className="h-full flex flex-col bg-gradient-to-br from-ink-900 via-ink-800 to-ink-900">
        <div className="flex items-center justify-end px-6 pt-3">
          <AuthButton variant="header" />
        </div>
        <div className="flex-1 overflow-hidden">
          <Dashboard
            user={user}
            teacherName={TEACHER_NAME}
            onStart={() => setView("lesson")}
          />
        </div>
      </div>
    );
  }

  if (view === "lesson" && user) {
    return (
      <Lesson
        user={user}
        teacherName={TEACHER_NAME}
        systemPromptBase={SYSTEM_PROMPT}
        onExit={() => setView("dashboard")}
      />
    );
  }

  return (
    <div className="h-full flex items-center justify-center bg-ink-900 text-ink-300">
      Unexpected state.
    </div>
  );
}
